import React, { useEffect, useState } from 'react';
import Icon from '@/components/Icon';
import { ApiError, api } from '@/lib/api';
import RichTextarea from '@/components/RichTextarea';

/**
 * Создание/редактирование «опросного периода» выбора внеурочки.
 *
 * Поля: даты начала действия и окна сбора + дата завершения, минимум и
 * максимум выбираемых занятий, текст-инструкция родителю и архив.
 */
const EMPTY = {
  dateStartPeriod:    '',
  dateStartSelection: '',
  dateEnd:            '',
  selectionMin:       '',
  selectionMax:       '',
  comment:            '',
  archive:            false,
};

export default function ExtracurricularSelectionPeriodEditModal({
  mode, period, onClose, onSaved, onDeleted,
}) {
  const [data, setData]     = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState(null);

  useEffect(() => {
    if (mode === 'edit' && period) {
      setData({
        dateStartPeriod:    period.dateStartPeriod    || '',
        dateStartSelection: period.dateStartSelection || '',
        dateEnd:            period.dateEnd            || '',
        selectionMin:       period.selectionMin > 0 ? String(period.selectionMin) : '',
        selectionMax:       period.selectionMax > 0 ? String(period.selectionMax) : '',
        comment:            period.comment || '',
        archive:            !!period.archive,
      });
    } else {
      setData(EMPTY);
    }
  }, [mode, period?.id]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && !saving) onClose(); };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [onClose, saving]);

  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true); setError(null);
    try {
      const payload = {
        dateStartPeriod:    data.dateStartPeriod,
        dateStartSelection: data.dateStartSelection,
        dateEnd:            data.dateEnd,
        selectionMin:       data.selectionMin === '' ? 0 : (parseInt(data.selectionMin, 10) || 0),
        selectionMax:       data.selectionMax === '' ? 0 : (parseInt(data.selectionMax, 10) || 0),
        comment:            data.comment.trim(),
        archive:            data.archive,
      };
      const res = mode === 'create'
        ? await api.post('/api/extracurricular-selection-periods', payload)
        : await api.put(`/api/extracurricular-selection-periods/${period.id}`, payload);
      onSaved?.(res.period);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Не удалось сохранить');
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async () => {
    if (!period) return;
    if (!confirm('Удалить период? Если по нему уже собраны заявки — операция отменится; в этом случае проще перевести в архив.')) return;
    setSaving(true); setError(null);
    try {
      await api.delete('/api/extracurricular-selection-periods/' + period.id);
      onDeleted?.(period.id);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Не удалось удалить');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="sd-overlay" onClick={(e) => { if (e.target.classList.contains('sd-overlay') && !saving) onClose(); }}>
      <div className="sd-modal sd-modal--narrow" role="dialog" aria-modal="true">
        <div className="sd-edit__head">
          <h2>Период выбора внеурочных занятий</h2>
          <button className="sd-close" onClick={onClose} disabled={saving}>
            <Icon name="x" size={14} />
          </button>
        </div>

        <form onSubmit={submit} className="sd-edit__form" noValidate>
          <div className="sd-edit__body">
            <div className="sd-edit__field sd-edit__field--full">
              <label className="sd-edit__label">
                Дата начала выбора <span className="muted" style={{ fontWeight: 400 }}>
                  (с этой даты появляется возможность выбрать)
                </span>
              </label>
              <input type="date" className="sd-edit__input" required
                     value={data.dateStartSelection}
                     onChange={(e) => set('dateStartSelection', e.target.value)} />
            </div>
            <div className="sd-edit__field sd-edit__field--full">
              <label className="sd-edit__label">Дата начала действия выбора *</label>
              <input type="date" className="sd-edit__input" required
                     value={data.dateStartPeriod}
                     onChange={(e) => set('dateStartPeriod', e.target.value)} />
            </div>
            <div className="sd-edit__field sd-edit__field--full">
              <label className="sd-edit__label">Дата конца действия выбора *</label>
              <input type="date" className="sd-edit__input" required
                     value={data.dateEnd}
                     onChange={(e) => set('dateEnd', e.target.value)} />
            </div>

            <div className="sd-edit__field sd-edit__field--full">
              <label className="sd-edit__label">Минимум предметов для выбора</label>
              <input type="number" min="0" max="100" className="sd-edit__input"
                     value={data.selectionMin}
                     onChange={(e) => set('selectionMin', e.target.value)}
                     placeholder="0 — без ограничения снизу" />
            </div>
            <div className="sd-edit__field sd-edit__field--full">
              <label className="sd-edit__label">Максимум предметов для выбора</label>
              <input type="number" min="0" max="100" className="sd-edit__input"
                     value={data.selectionMax}
                     onChange={(e) => set('selectionMax', e.target.value)}
                     placeholder="0 — без ограничения сверху" />
            </div>

            <div className="sd-edit__field sd-edit__field--full">
              <label className="sd-edit__label">Текст на странице выбора</label>
              <RichTextarea
                value={data.comment}
                onChange={(html) => set('comment', html)}
                disabled={saving}
                minHeight={140}
                maxLength={2000}
                placeholder="Например: «Выберите внеурочные занятия, которые ваш ребёнок будет посещать с 1 сентября по 31 декабря 2026 года. Нужно выбрать от 4 до 6 занятий.»"
              />
            </div>

            {mode === 'edit' && (
              <div className="sd-edit__field sd-edit__field--full">
                <label className="ref-checkbox">
                  <input type="checkbox" checked={data.archive}
                         onChange={(e) => set('archive', e.target.checked)} />
                  <span>В архиве (скрыт из активных списков)</span>
                </label>
              </div>
            )}
          </div>

          <div className="sd-edit__foot">
            {error && <div className="sd-edit__error">{error}</div>}
            {mode === 'edit' && (
              <button type="button" className="btn btn--sm btn--danger"
                      onClick={onDelete} disabled={saving}>
                Удалить
              </button>
            )}
            <div className="sd-edit__foot-spacer" />
            <button type="button" className="btn btn--sm" onClick={onClose} disabled={saving}>Отмена</button>
            <button type="submit" className="btn btn--primary btn--sm" disabled={saving}>
              {saving ? 'Сохраняем…' : (mode === 'create' ? 'Создать' : 'Сохранить')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
